import type {
  WorkOrder,
  WorkOrderPriority,
  WorkOrderProvenance
} from '@axilon-apps/domains-ontology-ui-state'

const PRIORITY_RANK: Record<WorkOrderPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3
}

export function priorityRank(p: WorkOrderPriority | string): number {
  return PRIORITY_RANK[p as WorkOrderPriority] ?? 99
}

/** Worst-first, then newest first. */
export function sortWorkOrdersWorstFirst(orders: WorkOrder[]): WorkOrder[] {
  return [...orders].sort((a, b) => {
    const pr = priorityRank(a.priority) - priorityRank(b.priority)
    if (pr !== 0) return pr
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
}

export function sortWorkOrdersNewestFirst(orders: WorkOrder[]): WorkOrder[] {
  return [...orders].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
}

export function firstInstructionLine(instruction: string): string {
  const line = instruction.split(/\r?\n/).find((l) => l.trim().length > 0)
  return (line ?? instruction).trim()
}

export function relativeTimeAgo(iso: string | undefined | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60_000))
  if (minutes < 60) return `${minutes}m`
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`
  return `${Math.floor(minutes / 1440)}d`
}

export function relativeTimeAgoLong(iso: string | undefined | null): string {
  const short = relativeTimeAgo(iso)
  return short.length > 0 ? `${short} ago` : ''
}

const AGENT_SHORT: Record<string, string> = {
  rca: 'RCA',
  pm: 'PM',
  bottleneck: 'BNA',
  bna: 'BNA',
  yield_quality: 'YQ',
  yq: 'YQ'
}

const AGENT_LONG: Record<string, string> = {
  rca: 'RCA agent',
  pm: 'predictive-maintenance agent',
  bottleneck: 'bottleneck agent',
  bna: 'bottleneck agent',
  yield_quality: 'yield-quality agent',
  yq: 'yield-quality agent'
}

export function provenanceShort(p: WorkOrderProvenance | null | undefined): string | null {
  if (!p?.source_agent) return null
  const key = p.source_agent.trim().toLowerCase()
  return AGENT_SHORT[key] ?? p.source_agent.toUpperCase()
}

export function provenanceLong(p: WorkOrderProvenance | null | undefined): string | null {
  if (!p?.source_agent) return null
  const key = p.source_agent.trim().toLowerCase()
  return AGENT_LONG[key] ?? `${p.source_agent} agent`
}

export function provenanceDetail(p: WorkOrderProvenance | null | undefined): string {
  if (!p) return ''
  const bits: string[] = []
  if (p.source_insight_id) bits.push(`Insight ${p.source_insight_id}`)
  if (p.source_run_id) bits.push(`run ${p.source_run_id}`)
  if (p.source_action_item_table && p.source_action_item_id) {
    bits.push(`${p.source_action_item_table}/${p.source_action_item_id}`)
  }
  return bits.join(' · ')
}
